<script setup>
const store = generalStore()

const resolveCoverUrl = (raw) => {
  if (!raw) return null
  try {
    const path = typeof raw === 'string' ? JSON.parse(raw) : raw
    return `https://api2.salamisbayconti.com/storage/${path}`
  } catch {
    return `https://api2.salamisbayconti.com/storage/${raw}`
  }
}

const cover = computed(() => resolveCoverUrl(store.getLandingCover))
const coverMobile = computed(() => resolveCoverUrl(store.getLandingCoverMobile))

const heroStyle = computed(() => {
  const style = {}
  if (cover.value) {
    // Set directly (not just as a --var) so the browser's preload scanner
    // can find this URL straight in the HTML and start fetching it before
    // CSS is even parsed — this is the LCP image, that head start matters.
    style.backgroundImage = `url('${cover.value}')`
    // Kept as a var too, purely as the fallback source for the mobile
    // override below when no mobile_image_url is configured.
    style['--hero-bg'] = `url('${cover.value}')`
  }
  if (coverMobile.value) style['--hero-bg-mobile'] = `url('${coverMobile.value}')`
  return style
})

// Mobile/tablet reservation bar: pulled up by exactly a third of its own
// (content-driven, so it changes e.g. when the child-ages row appears)
// rendered height, so it always straddles the hero boundary 1/3 above,
// 2/3 below — and, since this is a real margin (not a transform), the
// section below follows right against its visible bottom edge with no
// leftover gap.
const resvFlowRef = ref(null)
const resvFlowOverlap = ref(0)
let resvFlowObserver

onMounted(() => {
  const el = resvFlowRef.value?.$el
  if (!el || typeof ResizeObserver === 'undefined') return
  resvFlowObserver = new ResizeObserver(([entry]) => {
    resvFlowOverlap.value = entry.contentRect.height / 3
  })
  resvFlowObserver.observe(el)
})
onBeforeUnmount(() => resvFlowObserver?.disconnect())
</script>

<template>
  <div class="slm-hero slm-hero-body" :style="heroStyle">
    <div class="slm-scrim-top"></div>
    <div class="slm-scrim-bottom"></div>

    <!-- Spacer for fixed header -->
    <div class="slm-hdr-spacer"></div>

    <!-- Reservation Bar -->
    <ResvBar
      class="container slm-resv"
      style="position: absolute; left: 15px; right: 15px; bottom: 30px;"
    />

  </div>

  <!-- Mobile/tablet: reservation bar sits in normal flow, between the
       landing (hero) section and the section below it, instead of
       floating over the hero image. Desktop is untouched — see .slm-resv
       above, hidden here only under the 1100px breakpoint. -->
  <ResvBar
    id-suffix="-m"
    ref="resvFlowRef"
    class="container slm-resv-flow"
    :style="resvFlowOverlap ? { marginTop: `-${resvFlowOverlap}px` } : null"
  />
</template>

<style scoped>
/* ─── Design tokens ─────────────────────────────────────── */
.slm-hero {
  --navy-950: #07151F;
  --navy-900: #0B1E2D;
  --navy-800: #122B3F;
  --gold-400: #E1C887;
  --gold-500: #D2B062;
  --gold-600: #C19A45;
  --gold-700: #A87C2E;
  --sand-100: #F5EEDF;
  --ink-500: #6E685B;
  --font-display: 'Cormorant Garamond', Georgia, serif;
  --font-serif: 'Cormorant Garamond', Georgia, serif;
  --font-sans: 'Cormorant Garamond', Georgia, serif;
  --radius-sm: 4px;
  --radius-md: 8px;
  --radius-pill: 999px;
  --scrim-top: linear-gradient(to bottom, rgba(7,21,31,.35), rgba(7,21,31,.12) 45%, rgba(7,21,31,0));
  --scrim-bottom: linear-gradient(to top, rgba(7,21,31,.45), rgba(7,21,31,.15) 50%, rgba(7,21,31,0));
  --glass-fill: rgba(12,30,45,.42);
  --glass-border: rgba(255,255,255,.22);
  --glass-blur: blur(14px) saturate(125%);
  --shadow-sm: 0 2px 8px rgba(20,30,45,.10);
  --shadow-glass: 0 12px 40px rgba(7,21,31,.30);
  --dur-fast: 160ms;
  --dur-base: 280ms;
  --ease-out: cubic-bezier(.22,.61,.36,1);
}

/* ─── Hero wrapper ──────────────────────────────────────── */
.slm-hero {
  position: relative;
  /* svh (not dvh) — dvh recalculates live as mobile browser chrome
     settles on load (fonts/images causing reflow too), and since
     ResvBar is absolutely positioned against this element's height,
     every recalculation visibly shook it. svh is a stable, one-time
     value. */
  min-height: 100svh;
  max-height: 800px;
  object-fit:cover;
  width: 100%;
  overflow: hidden;
  background-color: var(--navy-950);
  /* background-image is NOT set here — it's inlined directly on the
     element (see heroStyle in the script) so the browser's preload
     scanner can discover the LCP image straight from the HTML, before
     this stylesheet is even parsed. */
  background-position: center 38%;
  background-size: cover;
  background-repeat: no-repeat;
  display: flex;
  flex-direction: column;
  font-family: var(--font-sans);
  color: #fff;
  padding-bottom: 30px;
}

@media (max-width: 768px) {
  /* Overrides the inline background-image with the mobile-specific cover
     (falls back to the same desktop cover via --hero-bg when no
     mobile_image_url is set). !important is required to beat the inline
     style's specificity. */
  .slm-hero { background-image: var(--hero-bg-mobile, var(--hero-bg)) !important; }
}

.slm-scrim-top {
  position: absolute;
  inset: 0 0 auto 0;
  height: 44%;
  background: var(--scrim-top);
  pointer-events: none;
  z-index: 1;
}
.slm-scrim-bottom {
  position: absolute;
  inset: auto 0 0 0;
  height: 65%;
  background: var(--scrim-bottom);
  pointer-events: none;
  z-index: 1;
}

/* Spacer for fixed SiteHeader */
.slm-hdr-spacer { height: 118px; flex-shrink: 0; }
@media (max-width: 1100px) { .slm-hdr-spacer { height: 64px; } }

/* ─── Hero body ─────────────────────────────────────────── */
.slm-hero-body {
  position: relative;
  z-index: 1;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-end;
  padding: 1.5rem 15px 0;
}

/* position:absolute instead of flex margin-top:auto — the flex-auto
   approach recalculates continuously as 100dvh animates while the
   mobile browser chrome (address bar) collapses on load, causing a
   visible "start at top, glide down" jump. Anchoring directly to the
   hero's edges is stable from first paint regardless of dvh changes. */
.slm-resv {
  position: absolute;
  left: 15px;
  right: 15px;
  bottom: 30px;
  z-index: 2;
  transition: transform 0.3s ease;
}
@media (max-width: 1100px) {
  /* Mobile/tablet: the overlay copy is replaced by .slm-resv-flow below,
     placed in normal flow after the hero instead of floating over it. */
  .slm-resv { display: none; }
}

.slm-resv-flow { display: none; }
@media (max-width: 1100px) {
  .slm-resv-flow {
    display: block;
    position: relative;
    z-index: 21;
    /* Precise 1/3-above-the-boundary offset is applied inline once its
       real height is measured (see resvFlowOverlap in the script) — this
       is just a same-ballpark static fallback for the instant before JS
       measures it. */
    margin-top: -100px;
  }
}

/* Mobile: +10px vertical padding */
@media (max-width: 767px) {
  .slm-hero.slm-hero-body {
    padding-top: calc(1.5rem + 10px);
    padding-bottom: 10px;
  }
}
</style>
